// B2B / Detail akce (schválený mockup 5): harmonogram, checklist, cena a marže, lektoři.
// Fáze 3a: bez PDF nabídky, faktury a provize (přibudou ve fázích 3b, 3c a 7).
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CalendarCheck, ChevronLeft, Pencil, ArrowRightLeft, MapPin, Clock, Users, Building2, Check, ListChecks, Calculator, Plus, Trash2, UserRound } from "lucide-react";
import GradientHeader from "../../components/GradientHeader.jsx";
import Card from "../../components/Card.jsx";
import { Stranka, ErrorNote, SkeletonRows } from "../../components/ui.jsx";
import { Pil, RadekShrnuti, Cipy } from "../../components/sales/salesUi.jsx";
import { useUzkaObrazovka } from "../../lib/obrazovka.js";
import { toast } from "../../components/Toast.jsx";
import { confirmDialog } from "../../components/confirmHost.jsx";
import { kdyUdalost } from "../../components/sales/salesApi.js";
import { b2bGet, b2bPost } from "../../components/b2b/b2bApi.js";
import { StavAkcePil, kc, terminText } from "../../components/b2b/b2bUi.jsx";
import { AkceDialog, StavAkceDialog, HarmonogramDialog, NakladyDialog } from "../../components/b2b/AkceDialogy.jsx";

function Harmonogram({ h, uzka }) {
  if (!h.length) return <p className="text-sm text-slate-500">Harmonogram je prázdný. Tužkou přidáš bloky, třeba přednášku a měření InBody. Počet lidí se pak počítá z délky bloku, počtu měřicích míst a přestávek.</p>;
  const cas = (r) => (r.od && r.do ? `${r.od} až ${r.do}` : r.od || "čas neurčen");
  if (uzka) {
    return (
      <div className="-mx-4 -mb-4 sm:-mx-5 sm:-mb-5">
        {h.map((r, i) => (
          <div key={i} className={`px-4 py-3 ${i ? "border-t border-slate-100" : ""}`}>
            <div className="text-base font-bold tabular-nums text-slate-900">{cas(r)}</div>
            <div className="mt-0.5 text-[15px] leading-snug text-slate-800">{r.co || "bez popisu"}</div>
            {(r.kdo || r.kapacita != null) && (
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {r.kdo && <Pil ton="neutralni" ikona={Users} casti={r.kdo} />}
                {r.kapacita != null && <Pil ton="znacka" casti={`${r.kapacita} lidí`} />}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500"><th className="px-3 py-2">Čas</th><th className="px-3 py-2">Co se děje</th><th className="px-3 py-2">Kdo</th><th className="px-3 py-2">Kapacita</th></tr></thead>
        <tbody>
          {h.map((r, i) => (
            <tr key={i} className="border-t border-slate-100 align-top">
              <td className="whitespace-nowrap px-3 py-2.5 font-bold tabular-nums text-slate-900">{cas(r)}</td>
              <td className="px-3 py-2.5 text-slate-800">{r.co}</td>
              <td className="px-3 py-2.5 text-slate-600">{r.kdo}</td>
              <td className="px-3 py-2.5">{r.kapacita != null && <Pil ton="znacka" mala casti={`${r.kapacita} lidí`} />}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Checklist({ akce, onZmena }) {
  const [novy, setNovy] = useState("");
  const c = akce.checklist || [];
  const hotovo = c.filter((x) => x.hotovo).length;
  const uloz = (nove) => onZmena(nove);
  return (
    <Card title="Checklist" ikona={ListChecks} odznak={`${hotovo} z ${c.length}`}>
      <ul className="space-y-1">
        {c.map((x, i) => (
          <li key={i} className="flex min-h-[44px] items-center gap-2">
            <button type="button" role="checkbox" aria-checked={x.hotovo} className="flex min-h-[44px] flex-1 items-start gap-3 py-1.5 text-left"
              onClick={() => uloz(c.map((y, j) => (j === i ? { ...y, hotovo: !y.hotovo } : y)))}>
              <span className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md border-2 ${x.hotovo ? "border-brand bg-brand" : "border-slate-300 bg-white"}`}>
                {x.hotovo && <Check className="h-4 w-4 text-white" />}
              </span>
              <span className={`text-[15px] leading-snug ${x.hotovo ? "text-slate-500 line-through" : "font-semibold text-slate-900"}`}>{x.text}</span>
            </button>
            <button type="button" className="btn-ikona shrink-0" aria-label={`Odebrat položku ${x.text}`} title="Odebrat" onClick={() => uloz(c.filter((_, j) => j !== i))}><Trash2 className="h-4 w-4" /></button>
          </li>
        ))}
      </ul>
      <form className="mt-3 flex items-center gap-2" onSubmit={(e) => { e.preventDefault(); if (novy.trim()) { uloz([...c, { text: novy.trim(), hotovo: false }]); setNovy(""); } }}>
        <input className="field min-w-0 flex-1" value={novy} onChange={(e) => setNovy(e.target.value)} placeholder="Přidat položku" aria-label="Nová položka checklistu" />
        <button type="submit" className="btn-secondary shrink-0" disabled={!novy.trim()}><Plus className="h-4 w-4" />Přidat</button>
      </form>
    </Card>
  );
}

export default function DetailAkce() {
  const { id } = useParams();
  const navigate = useNavigate();
  const uzka = useUzkaObrazovka();
  const [d, setD] = useState(null);
  const [chyba, setChyba] = useState("");
  const [okno, setOkno] = useState(null);

  const nacti = useCallback(async () => {
    try { setD(await b2bGet("akce_detail", { id })); setChyba(""); }
    catch (e) { setChyba(e.message); }
  }, [id]);
  useEffect(() => { setD(null); nacti(); }, [nacti]);

  const zpetNa = d?.firma ? `/b2b/firma/${d.firma.id}` : "/b2b/akce";
  const zpet = <div><button type="button" className="btn-secondary" onClick={() => navigate(d?.firma ? zpetNa : "/b2b/akce")}><ChevronLeft className="h-4 w-4" />{d?.firma?.nazev || "Akce"}</button></div>;
  if (chyba) return <Stranka sirka="plna">{zpet}<ErrorNote onRetry={nacti}>{chyba}</ErrorNote></Stranka>;
  if (!d) return <Stranka sirka="plna">{zpet}<SkeletonRows rows={5} /></Stranka>;

  const a = d.akce;
  const t = terminText(a);
  const zavri = () => setOkno(null);
  const poZmene = () => { zavri(); nacti(); };
  const ulozPole = async (pole, ticho = true) => {
    try { await b2bPost("akce_upravit", { id: a.id, ...pole }); if (!ticho) toast("Uloženo.", "success"); nacti(); }
    catch (e) { toast(e.message, "error"); }
  };
  const smazat = async () => {
    const ok = await confirmDialog({ title: "Smazat akci?", description: `Nabídka ${a.nazev} se nenávratně smaže.`, confirmLabel: "Smazat", variant: "danger" });
    if (!ok) return;
    try { await b2bPost("akce_smazat", { id: a.id }); toast("Akce smazána.", "success"); navigate(zpetNa); }
    catch (e) { toast(e.message, "error"); }
  };

  const ikony = [
    { klic: "upravit", ikona: Pencil, popis: "Upravit akci", onClick: () => setOkno("upravit") },
    { klic: "stav", ikona: ArrowRightLeft, popis: "Změnit stav", onClick: () => setOkno("stav") },
    ...(a.stav === "nabidka" && !a.fapi_invoice_id ? [{ klic: "smazat", ikona: Trash2, popis: "Smazat akci", onClick: smazat }] : []),
  ];

  const lektoriVse = [...new Set([...(d.lektori_moznosti || []), ...a.lektori])];
  const prah = d.marze_prah ?? 30;
  const pod = a.marze_pct != null && a.marze_pct < prah;

  const harmonogram = (
    <Card title="Harmonogram" ikona={Clock} odznak={a.kapacita_celkem != null ? `${a.kapacita_celkem} měření` : undefined}
      action={<button type="button" className="btn-secondary btn-sm" onClick={() => setOkno("harmonogram")}><Pencil className="h-4 w-4" />Upravit</button>}>
      <Harmonogram h={a.harmonogram} uzka={uzka} />
      {!uzka && a.harmonogram.length > 0 && <p className="mt-2 text-xs text-slate-500">Počet lidí se počítá z délky bloku, počtu měřicích míst a přestávek.</p>}
    </Card>
  );
  const lektori = (
    <Card title="Lektoři" ikona={UserRound} odznak={a.lektori.length}>
      {lektoriVse.length === 0
        ? <p className="text-sm text-slate-500">V adminu zatím není žádný trenér ani uživatel, kterého jde vybrat.</p>
        : <Cipy vice moznosti={lektoriVse} value={a.lektori} onChange={(v) => ulozPole({ lektori: v })} />}
    </Card>
  );
  const cena = (
    <Card title="Cena a marže" ikona={Calculator} action={<button type="button" className="btn-secondary btn-sm" onClick={() => setOkno("naklady")}><Pencil className="h-4 w-4" />Náklady</button>}>
      <RadekShrnuti popisek="Cena bez DPH">{kc(a.cena_bez_dph)}</RadekShrnuti>
      <RadekShrnuti popisek={`DPH ${String(a.dph_sazba).replace(".", ",")} %`}>{a.dph == null ? "bez ceny" : kc(a.dph)}</RadekShrnuti>
      <RadekShrnuti popisek="Cena s DPH">{a.cena_s_dph == null ? "bez ceny" : kc(a.cena_s_dph)}</RadekShrnuti>
      <div className="mt-3 rounded-xl bg-slate-50 px-3 py-1">
        <div className="pt-2 text-xs font-bold uppercase tracking-wider text-slate-500">Náklady</div>
        {a.naklady.length === 0 && <p className="py-2 text-sm text-slate-500">Zatím žádné náklady.</p>}
        {a.naklady.map((n, i) => <RadekShrnuti key={i} popisek={n.nazev}><span className="whitespace-nowrap">{kc(n.castka)}</span></RadekShrnuti>)}
        {a.naklady.length > 0 && <RadekShrnuti popisek="Náklady celkem">{kc(a.naklady_celkem)}</RadekShrnuti>}
      </div>
      {a.marze != null && (
        <div className={`mt-2 flex items-center justify-between gap-3 rounded-xl border px-3 py-2.5 ${pod ? "border-amber-200 bg-amber-50" : "border-emerald-200 bg-emerald-50"}`}>
          <span className={`text-sm font-semibold ${pod ? "text-amber-900" : "text-emerald-900"}`}>Marže před provizí</span>
          <span className="text-right">
            <b className={`block text-xl tabular-nums ${pod ? "text-amber-800" : "text-emerald-800"}`}>{kc(a.marze)}</b>
            <span className={`text-xs font-semibold ${pod ? "text-amber-700" : "text-emerald-700"}`}>{String(a.marze_pct).replace(".", ",")} % z ceny, {pod ? `pod hranicí ${prah} %` : `nad hranicí ${prah} %`}</span>
          </span>
        </div>
      )}
    </Card>
  );
  const osa = d.aktivity.length > 0 && (
    <Card title="Historie akce" ikona={Clock}>
      <ul className="space-y-2">
        {d.aktivity.map((x) => { const k = kdyUdalost(x.kdy); return (
          <li key={x.id} className="text-sm text-slate-800"><span className="mr-2 text-xs tabular-nums text-slate-500">{[k.den, k.cas].filter(Boolean).join(" ")}</span>{x.text}</li>
        ); })}
      </ul>
    </Card>
  );

  return (
    <Stranka sirka="plna">
      {zpet}
      <GradientHeader title={a.nazev} icon={CalendarCheck}
        akce={ikony} />
      <div className="flex flex-wrap gap-1.5">
        <StavAkcePil stav={a.stav} />
        <Pil ton="neutralni" ikona={CalendarCheck} casti={[t.datum, t.cas].filter(Boolean)} />
        {d.produkt && <Pil ton="neutralni" ikona={Building2} casti={d.produkt.nazev} />}
        {a.pocet_lidi != null && <Pil ton="neutralni" ikona={Users} casti={`${a.pocet_lidi} lidí`} />}
        {a.misto && <Pil ton="neutralni" ikona={MapPin} casti={a.misto} />}
        {d.osoba && <Pil ton="neutralni" ikona={UserRound} casti={d.osoba.jmeno} />}
      </div>
      {a.poznamka && <p className="whitespace-pre-line text-sm text-slate-700">{a.poznamka}</p>}
      {uzka ? (
        <div className="space-y-4">{harmonogram}<Checklist akce={a} onZmena={(c) => ulozPole({ checklist: c })} />{lektori}{cena}{osa}</div>
      ) : (
        <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_420px]">
          <div className="space-y-4">{harmonogram}<Checklist akce={a} onZmena={(c) => ulozPole({ checklist: c })} />{osa}</div>
          <div className="space-y-4">{cena}{lektori}</div>
        </div>
      )}
      <AkceDialog open={okno === "upravit"} akce={a} firma={d.firma} osoby={d.osoby || []} onClose={zavri} onHotovo={poZmene} />
      <StavAkceDialog open={okno === "stav"} akce={a} firmaStav={d.firma?.stav} onClose={zavri} onHotovo={poZmene} />
      <HarmonogramDialog open={okno === "harmonogram"} akce={a} lektori={lektoriVse} onClose={zavri} onHotovo={poZmene} />
      <NakladyDialog open={okno === "naklady"} akce={a} onClose={zavri} onHotovo={poZmene} />
    </Stranka>
  );
}
